import PropTypes from "prop-types";

const NONE = "NONE";
const LOADING = "LOADING";
const CREATING = "CREATING";
const UPDATING = "UPDATING";
const DELETING = "DELETING";

// LoadObjects expose a promise for their work to facilitate migration of
// non-reactive flows (all the controller spaghetti!). They should NOT be used
// in new code.

// don't let clients construct new instances - use a factory!
const SECRET = "SECRET:" + Math.random();

class LoadObject {

    constructor(secret, state, hasValue, value, error, promise, propTypes) {
        if (secret !== SECRET) {
            throw new Error(
                "Construct LoadObjects via static LoadObject.empty()",
            );
        }
        this._operation = state;
        this._hasValue = hasValue;
        this._value = value;
        this._error = error;
        this._promise = promise;
        this._propTypes = propTypes;
    }

    getOperation() {
        return this._operation;
    }

    isPending() { // armus
        return this.hasOperation();
    }

    isDone() { // compat
        return !this.hasOperation();
    }

    hasOperation() { // compat
        return this._operation !== NONE;
    }

    isLoading() {
        return this._operation === LOADING;
    }

    isCreating() {
        return this._operation === CREATING;
    }

    isUpdating() {
        return this._operation === UPDATING;
    }

    isDeleting() {
        return this._operation === DELETING;
    }

    isEmpty() {
        return !this.hasValue() && !this.hasOperation() && !this.hasError();
    }

    map(work) {
        if (!this.hasValue()) {
            return this;
        }
        const curr = this.getValueEnforcing();
        const next = work(curr);
        if (curr === next) {
            return this;
        }
        return this.setValue(next);
    }

    setOperation(operation) { // compat
        throw new Error("setOperation is not supported, because Promise junk.");
    }

    setValue(value) {
        if (process.env.NODE_ENV !== "production" && this._propTypes) {
            const pt = this._propTypes;
            PropTypes.checkPropTypes(pt.typeSpecs, value, "key", pt.componentName);
        }
        return new LoadObject(
            SECRET,
            this._operation,
            true,
            value,
            this._error,
            this._promise,
            this._propTypes,
        );
    }

    clearValue() { // armus
        return this.removeValue();
    }

    removeValue() {
        if (this._value === undefined && this._hasValue === false) {
            return this;
        }
        return new LoadObject(
            SECRET,
            this._operation,
            false,
            undefined,
            this._error,
            this._promise,
            this._propTypes,
        );
    }

    hasValue() {
        return this._hasValue;
    }

    getValue() {
        return this._value;
    }

    getRequiredValue() { // armus
        return this.getValueEnforcing();
    }

    getValueEnforcing() { // compat
        if (!this.hasValue()) {
            throw new Error("This LoadObject does not have a value");
        }
        return this._value;
    }

    setError(error) {
        return new LoadObject(
            SECRET,
            this._operation,
            this._hasValue,
            this._value,
            error,
            undefined,
            this._propTypes,
        );
    }

    clearError() { // armus
        return this.removeError();
    }

    removeError() { // compat
        if (this._error === undefined) {
            return this;
        }
        return new LoadObject(
            SECRET,
            this._operation,
            this._hasValue,
            this._value,
            undefined,
            undefined,
            this._propTypes,
        );
    }

    hasError() {
        return this._error != null;
    }

    getError() {
        return this._error;
    }

    getRequiredError() { // armus
        return this.getErrorEnforcing();
    }

    getErrorEnforcing() { // compat
        if (!this.hasError()) {
            throw new Error("This LoadObject does not have an error");
        }
        return this._error;
    }

    promise() {
        if (this.isPending()) {
            return this._promise;
        }
        if (this.hasValue()) {
            return Promise.resolve(this.getValueEnforcing());
        }
        if (this.hasError()) {
            return Promise.reject(this.getErrorEnforcing());
        }
        throw new Error("Empty LoadObjects can't make promises");
    }

    loading(promise) {
        return new LoadObject(
            SECRET,
            LOADING,
            this._hasValue,
            this._value,
            this._error,
            promise,
            this._propTypes,
        );
    }

    creating(promise) {
        return new LoadObject(
            SECRET,
            CREATING,
            this._hasValue,
            this._value,
            this._error,
            promise,
            this._propTypes,
        );
    }

    updating(promise) {
        return new LoadObject(
            SECRET,
            UPDATING,
            this._hasValue,
            this._value,
            this._error,
            promise,
            this._propTypes,
        );
    }

    deleting(promise) {
        return new LoadObject(
            SECRET,
            DELETING,
            this._hasValue,
            this._value,
            this._error,
            promise,
            this._propTypes,
        );
    }

    done() {
        return this.removeOperation();
    }

    removeOperation() { // compat
        if (this._operation === NONE) {
            return this;
        }
        return new LoadObject(
            SECRET,
            NONE,
            this._hasValue,
            this._value,
            this._error,
            undefined,
            this._propTypes,
        );
    }

    receiveValue(value) {
        return this.done().setValue(value);
    }

    receiveError(error) {
        return this.done().setError(error);
    }

    withPropTypes(typeSpecs, componentName = "LoadObject") {
        if (process.env.NODE_ENV === "production") {
            return this;
        } else {
            if (this._hasValue) {
                PropTypes.checkPropTypes(typeSpecs, this._value, "key", componentName);
            }
            return new LoadObject(
                SECRET,
                this._operation,
                this._hasValue,
                this._value,
                this._error,
                this._promise,
                {
                    typeSpecs,
                    componentName,
                },
            );
        }
    }

    static load(promise) { // armus
        return LoadObject.loading(promise);
    }

    static loading(promise) { // compat
        return new LoadObject(SECRET, LOADING, false, undefined, undefined, promise);
    }

    static creating(promise) { // compat
        return new LoadObject(SECRET, CREATING, false, undefined, undefined, promise);
    }

    static updating(promise) { // compat
        return new LoadObject(SECRET, UPDATING, false, undefined, undefined, promise);
    }

    static deleting(promise) { // compat
        return new LoadObject(SECRET, DELETING, false, undefined, undefined, promise);
    }

    static empty() {
        return new LoadObject(SECRET, NONE, false);
    }

    static withValue(value) {
        return LoadObject.empty().setValue(value);
    }

    static withError(error) {
        return LoadObject.empty().setError(error);
    }
}

export default LoadObject;



// WEBPACK FOOTER //
// ./app/data/LoadObject.js